import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { SkeletonLoader } from 'components';
import { routes } from 'constants/index';
import { humanReadable } from 'utils';

import { CompanyCodeDiscrepanciesDialog } from 'modules/Dashboard/components';

import { DiscrepancyKey, OverviewUiProps, TicketCounts } from './Overview.types';

import './overview.scss';

const LIST_DEFAULTS = 'page=1&rows=50&first=0';

const listOneWith = (status: string) =>
  `${routes.listOne.index}?${LIST_DEFAULTS}&ticketStatus=${status}&ticketType=All`;
const listTwoWith = (status: string) =>
  `${routes.listTwo.index}?${LIST_DEFAULTS}&ticketStatus=${status}&ticketType=All`;

const SOURCE_DATA = routes.listZero.index;
const PARTICIPANTS = `${routes.listZero.index}/${routes.listZero.participants}`;
const INVOICES_NEED_APPROVAL = `${routes.listFive.index}/${routes.listFive.needApproval}`;
const INVOICES_OTHER = `${routes.listFive.index}/${routes.listFive.otherInvoices}`;

// Who has to act on each list: List 1 waits for AZADEA to fix the data, List 2
// for Clover to process the endorsement. Shown once, on the list's card.
const WITH_AZADEA = 'With AZADEA';
const WITH_CLOVER = 'With Clover';

const TYPE_NAMES: Record<string, string> = {
  AD: 'Addition',
  DE: 'Deletion',
};

const DISCREPANCY_NAMES: Record<DiscrepancyKey, string> = {
  list0Discrepancy: 'Source data',
  list3Discrepancy: 'Insured principals',
  policiesDiscrepancy: 'Policies',
  rbacPolicyHolderDiscrepancy: 'RBAC',
};

const format = (value: number) => value.toLocaleString();

const statusLine = (counts: TicketCounts) =>
  `${format(counts.pending)} pending · ${format(counts.clean)} clean · ${format(counts.errors)} errors`;

// Stands in for a figure whose request failed, so it never reads as zero.
function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <span className="overview__load-error" role="status">
      Couldn't load
      <button type="button" className="overview__retry" onClick={onRetry}>
        Retry
      </button>
    </span>
  );
}

function Card({
  icon,
  label,
  owner,
  value,
  detail,
  to,
  failed,
  onRetry,
}: {
  icon: string;
  label: string;
  owner?: string;
  value: number;
  detail: ReactNode;
  to: string;
  failed: boolean;
  onRetry: () => void;
}) {
  const body = (
    <>
      <span className="overview__card-icon" aria-hidden="true">
        <i className={`pi ${icon}`} />
      </span>
      <span className="overview__card-body">
        <span className="overview__card-label">{label}</span>
        {failed ? (
          <LoadError onRetry={onRetry} />
        ) : (
          <>
            <span className="overview__card-value">{format(value)}</span>
            <span className="overview__card-detail">{detail}</span>
          </>
        )}
        {owner && <span className="overview__card-owner">{owner}</span>}
      </span>
    </>
  );

  // A failed card holds a Retry button, so it cannot also be one big link.
  return failed ? (
    <div className="overview__card overview__card--failed">{body}</div>
  ) : (
    <Link to={to} className="overview__card">
      {body}
    </Link>
  );
}

// One bar per list, split by the kind of change each ticket carries.
function TypeBreakdown({ label, counts }: { label: string; counts: TicketCounts }) {
  const total = counts.byType.reduce((sum, { count }) => sum + count, 0);

  return (
    <div className="overview__breakdown">
      <div className="overview__breakdown-head">
        <span>{label}</span>
        <span className="overview__muted">{format(total)} open</span>
      </div>

      {total === 0 ? (
        <div className="overview__muted">No open tickets.</div>
      ) : (
        <>
          <div className="overview__bar" role="img" aria-label={`${label} by type`}>
            {counts.byType.map(({ type, count }) => (
              <span
                key={type}
                className={classNames(
                  'overview__bar-segment',
                  `overview__bar-segment--${type.toLowerCase()}`
                )}
                style={{ flexGrow: count }}
                title={`${TYPE_NAMES[type] || type}: ${format(count)}`}
              />
            ))}
          </div>
          <div className="overview__legend">
            {counts.byType.map(({ type, count }) => (
              <span key={type} className="overview__legend-item">
                <span
                  className={classNames(
                    'overview__dot',
                    `overview__dot--${type.toLowerCase()}`
                  )}
                  aria-hidden="true"
                />
                {TYPE_NAMES[type] || type}
                <strong>{format(count)}</strong>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function HealthItem({
  ok,
  icon,
  label,
  to,
  note,
  children,
}: {
  ok?: boolean;
  icon?: string;
  label: string;
  // A short line under the value, such as when it last succeeded.
  note?: string;
  // Where to go about it; the red items lead to the page that fixes them.
  to?: string;
  children: ReactNode;
}) {
  return (
    <div className="overview__health-item">
      {icon ? (
        <i className={`pi ${icon} overview__health-icon`} aria-hidden="true" />
      ) : (
        <span
          className={classNames('overview__health-dot', {
            'overview__health-dot--ok': ok,
            'overview__health-dot--bad': !ok,
          })}
          aria-hidden="true"
        />
      )}
      <div>
        <div className="overview__health-label">{label}</div>
        <div className="overview__health-value">
          {to ? (
            <Link to={to} className="overview__health-link">
              {children}
            </Link>
          ) : (
            children
          )}
        </div>
        {note && <div className="overview__health-note">{note}</div>}
      </div>
    </div>
  );
}

export default function OverviewUi({
  isLoading,
  data,
  errors,
  openDiscrepancy,
  handlers,
}: OverviewUiProps) {
  const { listOne, listTwo, followUps, insured, invoices, health } = data;
  const retry = handlers.handleRefresh;

  const discrepancyKeys = (
    Object.keys(health.discrepancies) as DiscrepancyKey[]
  ).filter((key) => health.discrepancies[key].length > 0);
  const discrepancyTotal = discrepancyKeys.reduce(
    (sum, key) => sum + health.discrepancies[key].length,
    0
  );

  const queue = [
    {
      icon: 'pi-times-circle',
      label: 'Request errors',
      count: listOne.errors,
      failed: errors.listOne,
      to: listOneWith('open-error'),
    },
    {
      icon: 'pi-replay',
      label: 'Addition follow-up errors',
      count: followUps.additionErrors,
      failed: errors.followUps,
      to: `${routes.listOne.index}/${routes.listOne.statuses}?ticketType=AD`,
    },
    {
      icon: 'pi-replay',
      label: 'Deletion follow-up errors',
      count: followUps.deletionErrors,
      failed: errors.followUps,
      to: `${routes.listOne.index}/${routes.listOne.statuses}?ticketType=DE`,
    },
    {
      icon: 'pi-clock',
      label: 'Pending requests',
      count: listOne.pending,
      failed: errors.listOne,
      to: listOneWith('open-pending'),
    },
    {
      icon: 'pi-exclamation-triangle',
      label: 'In-progress errors',
      count: listTwo.errors,
      failed: errors.listTwo,
      to: listTwoWith('open-error'),
    },
  ];

  return (
    <div className="overview">
      {isLoading ? (
        <SkeletonLoader height="40rem" />
      ) : (
        <>
          <div className="overview__cards">
            <Card
              icon="pi-file-edit"
              label="Pending Requests"
              owner={WITH_AZADEA}
              value={listOne.total}
              detail={statusLine(listOne)}
              to={listOneWith('All')}
              failed={errors.listOne}
              onRetry={retry}
            />
            <Card
              icon="pi-clock"
              label="In Progress"
              owner={WITH_CLOVER}
              value={listTwo.total}
              detail={statusLine(listTwo)}
              to={listTwoWith('All')}
              failed={errors.listTwo}
              onRetry={retry}
            />
            <Card
              icon="pi-users"
              label="Insured Principals"
              value={insured.total}
              detail={`${format(insured.withoutLife)} without life insurance`}
              to={`${routes.listThree.index}?${LIST_DEFAULTS}`}
              failed={errors.insured}
              onRetry={retry}
            />
          </div>

          <div className="overview__grid">
            <section className="overview__panel overview__panel--queue">
              <h2 className="overview__panel-title overview__panel-title--spaced">
                Needs Attention
              </h2>

              {/* Each row is the way into its list: the whole row is the link,
                  so there is no separate button to aim at. */}
              <ul className="overview__queue">
                {queue.map((row) => {
                  const content = (
                    <>
                      <span className="overview__queue-label">
                        <i className={`pi ${row.icon}`} aria-hidden="true" />
                        {row.label}
                      </span>
                      {row.failed ? (
                        <LoadError onRetry={retry} />
                      ) : (
                        <span className="overview__queue-count">
                          {format(row.count)}
                        </span>
                      )}
                    </>
                  );

                  return (
                    <li key={row.label}>
                      {!row.failed && row.count > 0 ? (
                        <Link to={row.to} className="overview__queue-row">
                          {content}
                          <i
                            className="pi pi-chevron-right overview__queue-chevron"
                            aria-hidden="true"
                          />
                        </Link>
                      ) : (
                        <div
                          className={classNames('overview__queue-row', {
                            'overview__queue-row--clear': !row.failed,
                          })}
                        >
                          {content}
                          <span className="overview__queue-chevron" />
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            <div className="overview__side">

              <section className="overview__panel">
                <h2 className="overview__panel-title">Invoices</h2>
                {errors.invoices ? (
                  <LoadError onRetry={retry} />
                ) : (
                  <div className="overview__invoices">
                    <Link to={INVOICES_NEED_APPROVAL} className="overview__invoice">
                      <span className="overview__invoice-label">
                        Awaiting your approval
                      </span>
                      <span className="overview__invoice-value">
                        {format(invoices.awaitingApproval)}
                        <i className="pi pi-chevron-right" aria-hidden="true" />
                      </span>
                      <span className="overview__muted">
                        {format(invoices.awaitingApprovalPrincipals)} principals ·{' '}
                        {format(invoices.awaitingApprovalDependents)} dependents
                      </span>
                    </Link>
                    <Link to={INVOICES_OTHER} className="overview__invoice">
                      <span className="overview__invoice-label">With Azadea</span>
                      <span className="overview__invoice-value">
                        {format(invoices.withAzadea)}
                        <i className="pi pi-chevron-right" aria-hidden="true" />
                      </span>
                      <span className="overview__muted">
                        {format(invoices.withAzadeaPrincipals)} principals ·{' '}
                        {format(invoices.withAzadeaDependents)} dependents
                      </span>
                    </Link>
                  </div>
                )}
              </section>

              <section className="overview__panel">
                <h2 className="overview__panel-title overview__panel-title--spaced">
                  Open Tickets
                </h2>
                {errors.listOne ? (
                  <LoadError onRetry={retry} />
                ) : (
                  <TypeBreakdown label="Pending Requests" counts={listOne} />
                )}
                {errors.listTwo ? (
                  <LoadError onRetry={retry} />
                ) : (
                  <TypeBreakdown label="In Progress" counts={listTwo} />
                )}
              </section>
            </div>
          </div>

          <section className="overview__panel overview__health">
            <h2 className="overview__panel-title">System Health</h2>
            <div className="overview__health-items">
              <HealthItem ok={health.apiOk && !errors.api} label="API status">
                {errors.api ? "Couldn't check" : health.apiOk ? 'OK' : 'Down'}
              </HealthItem>
              <HealthItem
                ok={health.sourceSyncOk && !errors.sync}
                label="Latest source sync"
                to={health.sourceSyncOk ? undefined : SOURCE_DATA}
                note={
                  !errors.sync && health.lastSync
                    ? `Last OK ${humanReadable(health.lastSync)}`
                    : undefined
                }
              >
                {errors.sync
                  ? "Couldn't check"
                  : health.sourceSyncOk
                  ? 'OK'
                  : 'Failed'}
              </HealthItem>
              {health.sourceDataBlocked !== undefined && (
                <HealthItem
                  ok={!health.sourceDataBlocked}
                  label="Source data submissions"
                  to={health.sourceDataBlocked ? SOURCE_DATA : undefined}
                >
                  {health.sourceDataBlocked ? 'Blocked' : 'Open'}
                </HealthItem>
              )}
              <HealthItem icon="pi-users" label="Participants" to={PARTICIPANTS}>
                {errors.sync ? "Couldn't load" : format(health.sourceRecords)}
              </HealthItem>
              <HealthItem
                ok={!errors.discrepancies && discrepancyTotal === 0}
                label="Data discrepancies"
              >
                {errors.discrepancies ? (
                  "Couldn't check"
                ) : discrepancyTotal === 0 ? (
                  'None'
                ) : (
                  <>
                    {format(discrepancyTotal)}{' '}
                    {discrepancyKeys.map((key) => (
                      <button
                        key={key}
                        type="button"
                        className="overview__link-button"
                        onClick={() => handlers.handleDiscrepancyOpen(key)}
                      >
                        ({DISCREPANCY_NAMES[key]})
                      </button>
                    ))}
                  </>
                )}
              </HealthItem>
              <Link
                to={`${routes.logs.index}?list=1&${LIST_DEFAULTS}`}
                className="overview__logs-link"
              >
                <i className="pi pi-history" aria-hidden="true" />
                View activity logs
              </Link>
            </div>
          </section>
        </>
      )}

      <CompanyCodeDiscrepanciesDialog
        title={
          openDiscrepancy
            ? `${DISCREPANCY_NAMES[openDiscrepancy]} discrepancies`
            : ''
        }
        onHide={handlers.handleDiscrepancyClose}
        data={openDiscrepancy ? health.discrepancies[openDiscrepancy] : []}
      />
    </div>
  );
}
